import { useEffect, useState } from 'react';
import { api, readErr } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/lib/toast';
import { fetchRegion, formatPrice, loadPaypalSdk } from '@/lib/region';
import { Wallet as WalletIcon, Loader2, Sparkles, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';

/**
 * Wallet page — Round 40i: region-aware (INR + USD via PayPal).
 *
 * India users continue with the proven Razorpay tile flow. International
 * users (USD currency) see USD tiles, USD bonus brackets, and tile
 * clicks open the PayPal Smart Buttons popup via the same shared loader
 * that RechargeProvider uses.
 */

const PRESET_FALLBACK_INR = [25, 50, 100, 250, 500, 1000, 2500, 5000, 10000];
const PRESET_FALLBACK_USD = [3, 5, 10, 25, 50, 100, 125, 150, 175, 200];

// Round 40o.2 — bonus-bracket fallbacks. Mirrors backend
// `WALLET_BONUS_BRACKETS` (services/pricing.py + routes/payments.py).
// Used as the initial state for `bonusBrackets` so the "+₹X bonus" /
// "+$X bonus" chips render under each tile immediately on first paint —
// even before /payments/wallet/tiers resolves, and even if it 401s
// (e.g. expired token). Without these the chips silently disappeared.
const BONUS_FALLBACK_INR = [
    { min_inr: 100,  max_inr: 500,    bonus_pct: 15 },
    { min_inr: 501,  max_inr: 1000,   bonus_pct: 30 },
    { min_inr: 1001, max_inr: null,   bonus_pct: 50 },
];
const BONUS_FALLBACK_USD = [
    { min_usd: 10,  max_usd: 50,     pct: 15 },
    { min_usd: 51,  max_usd: 150,    pct: 30 },
    { min_usd: 151, max_usd: null,   pct: 50 },
];

function bonusPctFor(amount, brackets, currency) {
    const amt = Number(amount || 0);
    const lkey = currency === 'USD' ? 'min_usd' : 'min_inr';
    const hkey = currency === 'USD' ? 'max_usd' : 'max_inr';
    for (const b of brackets || []) {
        const lo = Number(b[lkey] || 0);
        const hi = b[hkey] == null ? null : Number(b[hkey]);
        // pct can come in as `pct` (new USD brackets) or `bonus_pct` (legacy INR)
        const pct = Number(b.pct ?? b.bonus_pct ?? 0);
        if (amt >= lo && (hi === null || amt <= hi)) return pct;
    }
    return 0;
}

function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve();
        const s = document.createElement('script');
        s.src = 'https://checkout.razorpay.com/v1/checkout.js';
        s.onload = () => resolve();
        document.body.appendChild(s);
    });
}

const SOURCE_LABELS = {
    signup: 'Welcome bonus',
    migration: 'Migration credit',
    recharge: 'Recharge',
    recharge_bonus: 'Recharge bonus',
    referral: 'Referral reward',
    chat_tick: 'Chat with Guruji',
    chat_refund: 'Chat refund',
    btr: 'Birth-time analysis',
    compatibility: 'Compatibility reading',
    admin_adjust: 'Admin adjustment',
};

export default function Wallet() {
    const { user, refresh } = useAuth();
    const [balanceInr, setBalanceInr] = useState(null);
    const [region, setRegion] = useState({ currency: 'INR', symbol: '₹', country: 'IN' });
    const [presets, setPresets] = useState(PRESET_FALLBACK_INR);
    const [bonusBrackets, setBonusBrackets] = useState(BONUS_FALLBACK_INR);
    const [customAmount, setCustomAmount] = useState('');
    const [recharging, setRecharging] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [txLoading, setTxLoading] = useState(true);
    const [paypalConfig, setPaypalConfig] = useState(null);

    const reloadBalance = async () => {
        try {
            const { data } = await api.get('/wallet/balance');
            setBalanceInr(Number(data?.balance_inr ?? 0));
        } catch (e) { /* handled by Protected */ }
    };

    const reloadTransactions = async () => {
        setTxLoading(true);
        try {
            const { data } = await api.get('/wallet/transactions?limit=20');
            setTransactions(data?.transactions || []);
        } catch (e) {
            setTransactions([]);
        } finally {
            setTxLoading(false);
        }
    };

    useEffect(() => {
        (async () => {
            const r = await fetchRegion();
            setRegion(r);
            // Round 40o.2 — flip bonus-bracket fallback to USD when the
            // region resolves international, so chips stay correct even
            // before /payments/wallet/tiers returns.
            if (r.currency === 'USD') {
                setPresets(PRESET_FALLBACK_USD);
                setBonusBrackets(BONUS_FALLBACK_USD);
            }
            try {
                const { data } = await api.get(`/payments/wallet/tiers?currency=${r.currency}`);
                if (r.currency === 'USD') {
                    setPresets(Array.isArray(data?.presets_usd) && data.presets_usd.length ? data.presets_usd : PRESET_FALLBACK_USD);
                    setBonusBrackets(Array.isArray(data?.bonus_brackets_usd) && data.bonus_brackets_usd.length ? data.bonus_brackets_usd : BONUS_FALLBACK_USD);
                } else {
                    setPresets(Array.isArray(data?.presets_inr) && data.presets_inr.length ? data.presets_inr : PRESET_FALLBACK_INR);
                    setBonusBrackets(Array.isArray(data?.bonus_brackets) && data.bonus_brackets.length ? data.bonus_brackets : BONUS_FALLBACK_INR);
                }
            } catch (e) { /* fall back to inline defaults already set */ }
            if (r.currency === 'USD') {
                try {
                    const { data } = await api.get('/payments/paypal/config');
                    setPaypalConfig(data);
                } catch { /* noop */ }
            }
        })();
        reloadBalance();
        reloadTransactions();

        // INR users: auto-reconcile any stuck Razorpay recharges.
        api.post('/payments/wallet/recharge/reconcile')
            .then(({ data }) => {
                const n = data?.recovered_count || 0;
                if (n > 0) {
                    const total = data.recovered.reduce(
                        (s, r) => s + Number(r.amount_inr || 0), 0,
                    );
                    toast.success(`Recovered ${n} stuck payment${n === 1 ? '' : 's'} — ₹${total.toFixed(2)} credited to your wallet.`);
                    reloadBalance();
                    reloadTransactions();
                    refresh().catch(() => {});
                }
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onRecharge = async (amount) => {
        const amt = Number(amount);
        const min = region.currency === 'USD' ? 3 : 25;
        if (!amt || amt < min) {
            toast.error(`Minimum recharge is ${region.symbol}${min}.`);
            return;
        }
        if (region.currency === 'USD') return onPaypalRecharge(amt);
        return onRazorpayRecharge(amt);
    };

    const onRazorpayRecharge = async (amt) => {
        setRecharging(true);
        try {
            const { data: orderRes } = await api.post('/payments/wallet/recharge/order', { amount_inr: amt });
            await loadRazorpayScript();
            const rzp = new window.Razorpay({
                key: orderRes.key_id,
                amount: orderRes.amount,
                currency: orderRes.currency,
                name: 'AstroConsult',
                description: `Wallet recharge ₹${amt}`,
                order_id: orderRes.order_id,
                prefill: { email: user?.email, name: user?.name },
                theme: { color: '#f59e0b' },
                // Round 48g — see RechargeProvider.jsx for full rationale.
                // Minimal config lets Razorpay's own device detection
                // pick the right method set on iOS Safari (custom
                // display.blocks was silently dropping UPI).
                retry: { enabled: true, max_count: 3 },
                handler: async (response) => {
                    try {
                        const { data: vres } = await api.post('/payments/wallet/recharge/verify', {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                        });
                        const bonusInr = Number(vres.bonus_inr || 0);
                        const totalCredited = Number(vres.total_credited_inr ?? vres.amount_inr ?? amt);
                        const msg = bonusInr > 0
                            ? `Wallet topped up: ₹${totalCredited} credited (₹${bonusInr} bonus). New balance: ₹${vres.wallet_balance_inr}`
                            : `Wallet topped up. New balance: ₹${vres.wallet_balance_inr}`;
                        toast.success(msg);
                        setCustomAmount('');
                        setBalanceInr(Number(vres.wallet_balance_inr ?? 0));
                        await reloadTransactions();
                        await refresh();
                    } catch (err) {
                        toast.error(readErr(err, 'Verification failed — please contact support'));
                    }
                },
                modal: { ondismiss: () => setRecharging(false) },
            });
            rzp.open();
        } catch (e) {
            toast.error(readErr(e, 'Could not start recharge'));
        } finally {
            setRecharging(false);
        }
    };

    const onPaypalRecharge = async (amountUsd) => {
        if (!paypalConfig?.client_id) {
            toast.error('PayPal not configured — please contact support.');
            return;
        }
        setRecharging(true);
        try {
            const { data: orderRes } = await api.post('/payments/paypal/order', {
                amount_usd: amountUsd, purpose: 'wallet_recharge',
            });
            const paypal = await loadPaypalSdk({ clientId: paypalConfig.client_id, currency: 'USD' });
            if (!paypal) throw new Error('PayPal SDK failed to load');

            await new Promise((resolve, reject) => {
                let mountNode = document.getElementById('paypal-mount-node');
                if (!mountNode) {
                    mountNode = document.createElement('div');
                    mountNode.id = 'paypal-mount-node';
                    mountNode.style.cssText = 'position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);z-index:9999;background:white;padding:16px;border-radius:8px;box-shadow:0 8px 32px rgba(0,0,0,.4);min-width:300px;';
                    const header = document.createElement('div');
                    header.style.cssText = 'font-size:13px;color:#475569;margin-bottom:10px;text-align:center;';
                    header.textContent = `Pay $${Number(amountUsd).toFixed(2)} with PayPal`;
                    mountNode.appendChild(header);
                    const buttonsDiv = document.createElement('div');
                    buttonsDiv.id = 'paypal-buttons-container';
                    mountNode.appendChild(buttonsDiv);
                    const cancel = document.createElement('button');
                    cancel.textContent = 'Cancel';
                    cancel.style.cssText = 'margin-top:10px;background:transparent;border:0;color:#94a3b8;font-size:12px;cursor:pointer;width:100%;';
                    cancel.onclick = () => { try { document.body.removeChild(mountNode); } catch { /* noop */ } reject(new Error('cancelled')); };
                    mountNode.appendChild(cancel);
                    document.body.appendChild(mountNode);
                }
                paypal.Buttons({
                    style: { layout: 'vertical', shape: 'rect', color: 'gold', label: 'paypal' },
                    createOrder: () => orderRes.order_id,
                    onApprove: async (data) => {
                        try {
                            const { data: cap } = await api.post('/payments/paypal/order/capture', { order_id: data.orderID });
                            try { document.body.removeChild(mountNode); } catch { /* noop */ }
                            const bonusUsd = Number(orderRes.bonus_usd || 0);
                            const msg = bonusUsd > 0
                                ? `Wallet topped up: $${(orderRes.amount_usd + bonusUsd).toFixed(2)} credited ($${bonusUsd.toFixed(2)} bonus).`
                                : `Wallet topped up.`;
                            toast.success(msg);
                            setCustomAmount('');
                            setBalanceInr(Number(cap.wallet_balance_inr ?? 0));
                            await reloadTransactions();
                            await refresh();
                            resolve();
                        } catch (err) {
                            toast.error(readErr(err, 'Capture failed — we’ll retry via webhook.'));
                            reject(err);
                        }
                    },
                    onError: (err) => {
                        try { document.body.removeChild(mountNode); } catch { /* noop */ }
                        toast.error('PayPal error — please try again.');
                        reject(err);
                    },
                    onCancel: () => {
                        try { document.body.removeChild(mountNode); } catch { /* noop */ }
                        reject(new Error('cancelled'));
                    },
                }).render('#paypal-buttons-container').catch(reject);
            });
        } catch (e) {
            if (e?.message !== 'cancelled') {
                toast.error(readErr(e, 'Could not start PayPal recharge'));
            }
        } finally {
            setRecharging(false);
        }
    };

    const customPct = bonusPctFor(Number(customAmount), bonusBrackets, region.currency);
    const customBonus = Math.round((Number(customAmount) || 0) * customPct) / 100;
    const isUsd = region.currency === 'USD';
    const minRecharge = isUsd ? 3 : 25;

    return (
        <div className="max-w-5xl mx-auto px-6 py-10 md:py-16" data-testid="wallet-page">
            {/* Header */}
            <div className="mb-10">
                <div className="overline mb-3">Account</div>
                <h1 className="font-serif text-4xl md:text-5xl text-parchment">Wallet</h1>
            </div>

            {/* Balance + bonus tiers */}
            <div className="grid md:grid-cols-2 gap-5 mb-10">
                <Card
                    data-testid="wallet-balance-card"
                    className="rounded-sm p-7 bg-elevated border-marigold/20"
                >
                    <div className="flex items-center gap-2 mb-2">
                        <WalletIcon size={16} className="text-marigold" />
                        <div className="overline text-marigold/80">Current balance</div>
                        {isUsd && (
                            <span className="ml-auto text-[10px] text-slate-500 uppercase tracking-wider border border-marigold/20 rounded-full px-2 py-0.5" data-testid="wallet-region-badge">
                                {region.country} · USD
                            </span>
                        )}
                    </div>
                    <div className="flex items-baseline gap-3 mt-3">
                        <div
                            data-testid="wallet-balance"
                            className="font-serif text-5xl text-parchment"
                        >
                            {balanceInr === null
                                ? '…'
                                : formatPrice(balanceInr, region.currency)}
                        </div>
                        <div className="text-sm text-slate-500">
                            · {isUsd ? '$1' : '₹25'} / minute of live chat with Guruji
                        </div>
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                        Wallet balance never expires. Use it for live chat, compatibility readings ({isUsd ? '$6' : '₹500'}) or birth-time analysis ({isUsd ? '$24' : '₹2,000'}).
                    </div>
                </Card>
                <Card
                    data-testid="wallet-bonus-card"
                    className="rounded-sm p-7 bg-marigold/5 border-marigold/40"
                >
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles size={16} className="text-marigold" />
                        <div className="overline text-marigold/80">Bonus on every recharge</div>
                    </div>
                    <ul className="text-sm text-parchment space-y-1.5">
                        {isUsd ? (
                            <>
                                <li>$10 – $50 <span className="text-marigold ml-1">+15% extra</span></li>
                                <li>$51 – $150 <span className="text-marigold ml-1">+30% extra</span></li>
                                <li>Above $150 <span className="text-marigold ml-1">+50% extra</span></li>
                            </>
                        ) : (
                            <>
                                <li>₹100 – ₹500 <span className="text-marigold ml-1">+15% extra</span></li>
                                <li>₹501 – ₹1,000 <span className="text-marigold ml-1">+30% extra</span></li>
                                <li>Above ₹1,000 <span className="text-marigold ml-1">+50% extra</span></li>
                            </>
                        )}
                    </ul>
                </Card>
            </div>

            {/* Recharge tiles */}
            <div className="mb-10" data-testid="wallet-recharge-section">
                <h2 className="font-serif text-2xl text-parchment mb-4">Recharge wallet</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {presets.map((amt) => {
                        const pct = bonusPctFor(amt, bonusBrackets, region.currency);
                        const bonus = Math.round(amt * pct) / 100;
                        return (
                            <button
                                key={amt}
                                disabled={recharging}
                                onClick={() => onRecharge(amt)}
                                data-testid={`wallet-recharge-tier-${amt}`}
                                className="relative px-3 py-4 rounded-sm border border-marigold/30 bg-elevated hover:border-marigold hover:bg-marigold/10 text-parchment text-base font-medium transition-colors disabled:opacity-50"
                            >
                                <div>{formatPrice(amt, region.currency)}</div>
                                {pct > 0 && (
                                    <div
                                        data-testid={`wallet-recharge-tier-${amt}-bonus`}
                                        className="mt-1 text-[10px] text-emerald-300 font-medium"
                                    >
                                        +{formatPrice(bonus, region.currency)} bonus
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Custom amount */}
                <div className="mt-6 max-w-md">
                    <div className="text-xs text-slate-400 mb-2">
                        Or enter a custom amount (min {formatPrice(minRecharge, region.currency)}{isUsd ? '' : ', max ₹50,000'})
                    </div>
                    <div className="flex gap-2">
                        <Input
                            type="number"
                            min={minRecharge}
                            value={customAmount}
                            onChange={(e) => setCustomAmount(e.target.value)}
                            placeholder={`Amount in ${region.symbol}`}
                            data-testid="wallet-recharge-custom-input"
                            className="bg-elevated border-marigold/30 text-parchment"
                        />
                        <Button
                            onClick={() => onRecharge(Number(customAmount))}
                            disabled={recharging || !customAmount || Number(customAmount) < minRecharge}
                            data-testid="wallet-recharge-custom-btn"
                            className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-sm"
                        >
                            {recharging ? <Loader2 className="animate-spin" size={14} /> : 'Recharge'}
                        </Button>
                    </div>
                    {customPct > 0 && (
                        <div
                            data-testid="wallet-recharge-custom-bonus"
                            className="mt-2 text-xs text-emerald-300"
                        >
                            +{formatPrice(customBonus, region.currency)} bonus ({customPct}% extra)
                        </div>
                    )}
                </div>
            </div>

            {/* Wallet ledger */}
            <div data-testid="wallet-ledger">
                <h2 className="font-serif text-2xl text-parchment mb-4">Recent activity</h2>
                {txLoading ? (
                    <div className="flex items-center gap-2 text-slate-500">
                        <Loader2 className="animate-spin" size={14} /> Loading…
                    </div>
                ) : transactions.length === 0 ? (
                    <Card className="bg-elevated border-marigold/15 rounded-sm p-6 text-center text-slate-500" data-testid="wallet-ledger-empty">
                        No transactions yet. Your first recharge will appear here.
                    </Card>
                ) : (
                    <div className="space-y-2">
                        {transactions.map((tx) => (
                            <div
                                key={tx.id}
                                data-testid={`wallet-tx-${tx.id}`}
                                className="flex items-center gap-3 px-4 py-3 rounded-sm bg-surface border border-marigold/10"
                            >
                                {tx.type === 'credit' ? (
                                    <ArrowDownCircle size={18} className="text-emerald-400 shrink-0" />
                                ) : (
                                    <ArrowUpCircle size={18} className="text-rose-400 shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-parchment truncate">
                                        {SOURCE_LABELS[tx.source] || tx.source || 'Transaction'}
                                        {tx.notes && <span className="text-slate-500 text-xs ml-2">· {tx.notes}</span>}
                                    </div>
                                    <div className="text-[11px] text-slate-500">
                                        {tx.created_at ? new Date(tx.created_at).toLocaleString() : ''}
                                    </div>
                                </div>
                                <div className={`text-sm font-medium ${tx.type === 'credit' ? 'text-emerald-400' : 'text-rose-400'}`}>
                                    {tx.type === 'credit' ? '+' : '−'}{formatPrice(Math.abs(Number(tx.amount_inr ?? tx.amount_paise / 100 ?? 0)), tx.currency || 'INR')}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="mt-10 pt-6 border-t border-marigold/10 text-xs text-slate-500" data-testid="wallet-provider-note">
                Payments are handled securely by {isUsd ? 'PayPal' : 'Razorpay'}. Your card details never touch our servers.
            </div>
        </div>
    );
}
